import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Snowflake, Lock, LogOut, Inbox, RefreshCw, Phone, Mail, Trash2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "jk_admin_token";

export default function Admin() {
  useEffect(() => { document.title = "Staff Dashboard | Juli Klein's A/C Services"; }, []);
  const [token, setToken] = useState(() => localStorage.getItem(KEY) || "");
  const [passcode, setPasscode] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/service-requests`, { headers: { "x-admin-token": t } });
      setRows(res.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem(KEY); setToken(""); toast.error("Session expired. Sign in again."); }
      else toast.error("Failed to load requests.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/verify`, { passcode });
      if (res.data.valid) { localStorage.setItem(KEY, passcode); setToken(passcode); toast.success("Welcome back, Juli!"); }
      else toast.error("Incorrect passcode.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Login failed."); }
  };

  const logout = () => { localStorage.removeItem(KEY); setToken(""); setRows([]); };

  const remove = async (id) => {
    if (!window.confirm("Delete this service request? This cannot be undone.")) return;
    const prev = rows;
    setRows(rows.filter((r) => r.id !== id));
    try {
      await axios.delete(`${API}/service-requests/${id}`, { headers: { "x-admin-token": token } });
      toast.success("Request deleted.");
    } catch {
      setRows(prev);
      toast.error("Could not delete. Please try again.");
    }
  };

  const clearAll = async () => {
    if (!window.confirm(`Delete ALL ${rows.length} service requests? This cannot be undone.`)) return;
    const prev = rows;
    setRows([]);
    try {
      await axios.delete(`${API}/service-requests`, { headers: { "x-admin-token": token } });
      toast.success("Inbox cleared.");
    } catch {
      setRows(prev);
      toast.error("Could not clear inbox. Please try again.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-charcoal px-5">
        <form data-testid="admin-login-form" onSubmit={login} className="w-full max-w-sm rounded-3xl bg-bone p-8 shadow-2xl">
          <div className="flex items-center gap-2.5 mb-6">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-terracotta text-bone"><Snowflake size={20} /></span>
            <span className="font-heading text-2xl font-semibold text-charcoal">Staff Login</span>
          </div>
          <label className="text-sm font-medium text-ink flex items-center gap-2 mb-2"><Lock size={14} /> Passcode</label>
          <input data-testid="admin-passcode-input" type="password" value={passcode} onChange={(e) => setPasscode(e.target.value)}
            placeholder="Enter passcode" className="w-full rounded-xl border border-sand bg-white px-4 py-3 outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/30 transition-all duration-200" />
          <button data-testid="admin-login-btn" type="submit" className="mt-5 w-full rounded-full bg-terracotta px-6 py-3.5 font-semibold text-bone hover:bg-terracotta-dark transition-colors duration-200">Sign In</button>
          <Link to="/" className="mt-4 block text-center text-sm text-ink hover:text-terracotta transition-colors">← Back to site</Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <header className="border-b border-sand bg-white sticky top-0 z-10">
        <div className="mx-auto max-w-6xl px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-terracotta text-bone"><Snowflake size={18} /></span>
            <span className="font-heading text-xl font-semibold">Service Requests</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh-btn" onClick={() => load(token)} className="inline-flex items-center gap-2 rounded-full border border-sand px-4 py-2 text-sm font-medium hover:bg-sand transition-colors">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            {rows.length > 0 && (
              <button data-testid="admin-clear-all-btn" onClick={clearAll} className="inline-flex items-center gap-2 rounded-full border border-red-200 text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-50 transition-colors">
                <Trash2 size={14} /> Clear all
              </button>
            )}
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-charcoal px-4 py-2 text-sm font-medium text-bone hover:bg-charcoal/90 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 md:px-8 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="rounded-2xl bg-white border border-sand p-5">
            <p className="text-xs uppercase tracking-widest text-ink">Total requests</p>
            <p className="font-heading text-4xl text-charcoal mt-1">{rows.length}</p>
          </div>
          <div className="rounded-2xl bg-white border border-sand p-5">
            <p className="text-xs uppercase tracking-widest text-ink">New</p>
            <p className="font-heading text-4xl text-terracotta mt-1">{rows.filter(r => r.status === "new").length}</p>
          </div>
          <div data-testid="admin-discount-stat" className="rounded-2xl bg-white border border-terracotta/40 p-5">
            <p className="text-xs uppercase tracking-widest text-ink flex items-center gap-1.5"><ShieldCheck size={13} className="text-terracotta" /> 10% discount</p>
            <p className="font-heading text-4xl text-charcoal mt-1">{rows.filter(r => r.discount_eligible).length}</p>
          </div>
          <div className="rounded-2xl bg-sage text-bone p-5">
            <p className="text-xs uppercase tracking-widest text-bone/70">Business phone</p>
            <p className="font-heading text-2xl mt-1">(239) 671-4867</p>
          </div>
        </div>

        {rows.length === 0 ? (
          <div data-testid="admin-empty" className="rounded-2xl border border-dashed border-sand bg-white p-16 text-center">
            <Inbox size={40} className="mx-auto text-sage-light" />
            <p className="mt-4 font-heading text-2xl text-charcoal">No requests yet</p>
            <p className="text-ink mt-1">New service requests from your website will appear here.</p>
          </div>
        ) : (
          <div data-testid="admin-requests-table" className="overflow-hidden rounded-2xl border border-sand bg-white">
            <table className="w-full text-sm">
              <thead className="bg-sand/60 text-left text-ink">
                <tr>
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold hidden md:table-cell">Contact</th>
                  <th className="px-5 py-3 font-semibold hidden lg:table-cell">Location</th>
                  <th className="px-5 py-3 font-semibold">Service</th>
                  <th className="px-5 py-3 font-semibold hidden lg:table-cell">Message</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Received</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`admin-row-${r.id}`} className="border-t border-sand align-top">
                    <td className="px-5 py-4 font-medium text-charcoal">
                      {r.name}
                      {r.discount_eligible && (
                        <span data-testid={`admin-discount-badge-${r.id}`} className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-terracotta px-2.5 py-0.5 text-[11px] font-bold text-bone whitespace-nowrap">
                          <ShieldCheck size={11} /> 10% OFF · Veteran / First Responder
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 text-charcoal hover:text-terracotta"><Phone size={13} /> {r.phone}</a>
                      <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 text-ink hover:text-terracotta mt-1"><Mail size={13} /> {r.email}</a>
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-ink">
                      {r.address ? <span className="block text-charcoal">{r.address}</span> : null}
                      <span>{[r.city, r.zip].filter(Boolean).join(", ") || "—"}</span>
                    </td>
                    <td className="px-5 py-4"><span className="inline-block rounded-full bg-terracotta/10 text-terracotta-dark px-3 py-1 text-xs font-medium">{r.service}</span></td>
                    <td className="px-5 py-4 hidden lg:table-cell max-w-xs text-ink">{r.message || "—"}</td>
                    <td className="px-5 py-4 text-ink whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()} <span className="block text-xs text-sage-light">{new Date(r.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span></td>
                    <td className="px-5 py-4 text-right">
                      <button data-testid={`admin-delete-${r.id}`} onClick={() => remove(r.id)} aria-label="Delete request" title="Delete request"
                        className="inline-flex items-center justify-center h-9 w-9 rounded-full text-ink hover:bg-red-50 hover:text-red-600 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
